import React, { useState } from "react";
import { Users, ChevronDown, Quote, GitBranch } from "lucide-react";

const ROLE_STYLE = {
  protagonist: { color: "var(--color-blue-primary)", label: "主角", bg: "color-mix(in srgb, var(--color-blue-primary) 10%, transparent)" },
  antagonist: { color: "var(--color-error)", label: "反派", bg: "color-mix(in srgb, var(--color-error) 10%, transparent)" },
  supporting: { color: "var(--color-success)", label: "配角", bg: "color-mix(in srgb, var(--color-success) 10%, transparent)" },
  minor: { color: "var(--color-text-muted)", label: "次要", bg: "color-mix(in srgb, var(--color-text-muted) 10%, transparent)" },
};

const CharacterProfileResult = ({ data, theme }) => {
  const [expandedIdx, setExpandedIdx] = useState(0);
  if (!data) return null;

  const characters = data.characters || [];

  return (
    <div className="space-y-4">
      {/* 总览 */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Users size={16} style={{ color: theme.colors.accentPrimary }} />
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>角色档案</span>
          <span className="text-2xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>{characters.length} 个角色</span>
        </div>
        {data.summary && <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.summary}</p>}
      </div>

      {/* 角色卡片 */}
      {characters.map((char, i) => {
        const role = ROLE_STYLE[char.role] || ROLE_STYLE.supporting;
        const isOpen = expandedIdx === i;
        return (
          <div key={i} className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <button onClick={() => setExpandedIdx(isOpen ? -1 : i)} className="w-full flex items-center justify-between px-5 py-3 transition-all hover:bg-[var(--color-bg-tertiary)]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: role.bg, color: role.color }}>
                  {char.name?.charAt(0) || "?"}
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{char.name}</span>
                    <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: role.bg, color: role.color }}>{role.label}</span>
                  </div>
                  <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>{char.identity || char.age || ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {char.personality?.length > 0 && (
                  <div className="flex gap-1 max-w-[200px] overflow-hidden">
                    {char.personality.slice(0, 3).map((p, pi) => (
                      <span key={pi} className="text-2xs px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>{p}</span>
                    ))}
                  </div>
                )}
                <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "var(--color-text-muted)" }} />
              </div>
            </button>

            {isOpen && (
              <div className="px-5 pb-4 space-y-3 animate-in fade-in duration-200" style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                {/* 人物小传 */}
                {char.profile && (
                  <div className="pt-3">
                    <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{char.profile}</p>
                  </div>
                )}

                {/* 角色弧线 */}
                {char.arc && (
                  <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <div className="flex items-center gap-1.5 mb-1"><GitBranch size={11} style={{ color: role.color }} /><span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>角色弧线</span></div>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{char.arc}</p>
                  </div>
                )}

                {/* 人物关系 */}
                {char.relationships?.length > 0 && (
                  <div>
                    <span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>人物关系</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {char.relationships.map((rel, ri) => (
                        <div key={ri} className="flex items-center gap-1 px-2 py-1 rounded-lg text-2xs" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                          <span style={{ color: "var(--color-text-primary)" }}>{rel.target}</span>
                          <span style={{ color: "var(--color-text-muted)" }}>·</span>
                          <span style={{ color: role.color }}>{rel.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 代表台词 */}
                {char.memorableQuotes?.length > 0 && (
                  <div>
                    <span className="text-2xs font-bold" style={{ color: "var(--color-text-muted)" }}>代表台词</span>
                    <div className="space-y-1 mt-1.5">
                      {char.memorableQuotes.map((q, qi) => (
                        <div key={qi} className="flex items-start gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                          <Quote size={10} className="mt-0.5 shrink-0" style={{ color: "var(--color-text-muted)" }} />
                          <span className="text-xs italic" style={{ color: "var(--color-text-secondary)" }}>{q}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CharacterProfileResult;
